/**
 * KSeF 2.0 API client (Phase 2 — direct e-invoice submission).
 */
import { apiClient } from './client';

export type KsefEnvironment = 'test' | 'demo' | 'prod';
export type KsefAuthMethod = 'token' | 'certificate';
export type KsefStatus = 'none' | 'processing' | 'accepted' | 'rejected' | 'na';

/**
 * The single source of truth for what each KSeF environment *is*.
 *
 * Every environment label, hostname and legal statement in the UI must come from
 * here. Previously the host was a hardcoded production literal in the submit modal
 * while the label came from a separate map, so the two could — and did — disagree:
 * a production submission was presented as "TEST · api.ksef.mf.gov.pl/v2".
 *
 * `tone` is deliberately *not* a severity. Production is the normal, successful
 * path — it is what the product is for — so it must never read as an error. It
 * gets `attention` (subtle amber) because it carries legal consequence, not
 * because anything is wrong. Green is reserved for the accepted-by-KSeF outcome.
 */
export const KSEF_ENV_META: Record<
  KsefEnvironment,
  { label: string; host: string; tone: 'neutral' | 'info' | 'attention'; legal: string }
> = {
  test: {
    label: 'Test',
    host: 'api-test.ksef.mf.gov.pl/v2',
    tone: 'neutral',
    legal: 'Sandbox. Invoices submitted here have no legal effect and can be re-sent freely.',
  },
  demo: {
    label: 'Demo',
    host: 'api-demo.ksef.mf.gov.pl/v2',
    tone: 'info',
    legal: 'Pre-production dry run. Behaves like production but has no legal effect.',
  },
  prod: {
    label: 'Production',
    host: 'api.ksef.mf.gov.pl/v2',
    tone: 'attention',
    legal:
      'Production. Once KSeF assigns a number the invoice is legally issued and cannot be '
      + 'deleted — mistakes can only be fixed with a corrective invoice.',
  },
};

export function isKsefEnvironment(value: unknown): value is KsefEnvironment {
  return value === 'test' || value === 'demo' || value === 'prod';
}

/** Shape shared by anything that can tell us which KSeF environment applies. */
interface KsefEnvSource {
  ksef?: { environment?: string | null } | null;
  seller?: { ksef?: { environment?: string | null } | null } | null;
}

/**
 * Resolve the environment an invoice belongs to, or `null` when it genuinely
 * cannot be determined.
 *
 * `invoice.ksef.environment` is only written at submit time, so it is null for
 * every invoice that has not been sent yet — which is exactly when the submit
 * modal opens. The seller's configured environment is what the backend will
 * actually use (`_service_for(seller)`), so that is the authoritative fallback.
 *
 * Never defaults to 'test'. Claiming "Test" on a production submission is the
 * bug this function exists to prevent.
 */
export function resolveKsefEnv(invoice?: KsefEnvSource | null): KsefEnvironment | null {
  const raw = invoice?.ksef?.environment ?? invoice?.seller?.ksef?.environment ?? null;
  return isKsefEnvironment(raw) ? raw : null;
}

export interface KsefCertificateInfo {
  subject: string | null;
  subject_nip: string | null;
  issuer: string | null;
  serial: string | null;
  fingerprint: string | null;
  valid_from: string | null;
  valid_to: string | null;
}

export interface KsefConnection {
  environment: KsefEnvironment;
  auth_method: KsefAuthMethod | null;
  token_scope: string;
  unlock_mode: 'each' | 'session';
  connected: boolean;
  connected_at: string | null;
  has_token: boolean;
  has_certificate: boolean;
  certificate: KsefCertificateInfo | null;
}

export interface KsefInvoiceState {
  status: KsefStatus;
  ksef_number: string | null;
  reference_number: string | null;
  session_reference: string | null;
  environment: string | null;
  submitted_at: string | null;
  accepted_at: string | null;
  error_code: string | null;
  error_description: string | null;
  has_upo: boolean;
}

export interface KsefActivityEvent {
  id: number;
  type: string;
  message: string | null;
  created_at: string | null;
}

export interface KsefSettingsResponse {
  seller_id: number;
  ksef: KsefConnection;
}

export interface KsefEligibility {
  plan_type: string;
  eligible: boolean;
}

export async function getKsefEligibility(): Promise<KsefEligibility> {
  const res = await apiClient.get<KsefEligibility>('/api/ksef/eligibility');
  return res.data;
}

export async function getKsefSettings(sellerId?: number): Promise<KsefSettingsResponse> {
  const qs = sellerId ? `?seller_id=${sellerId}` : '';
  const res = await apiClient.get<KsefSettingsResponse>(`/api/ksef/settings${qs}`);
  return res.data;
}

export async function updateKsefSettings(payload: {
  seller_id?: number;
  environment?: KsefEnvironment;
  auth_method?: KsefAuthMethod;
  token?: string;
  token_scope?: string;
  unlock_mode?: 'each' | 'session';
}): Promise<KsefSettingsResponse> {
  const res = await apiClient.put<KsefSettingsResponse>('/api/ksef/settings', payload);
  return res.data;
}

export async function uploadKsefCertificate(
  file: File,
  password: string,
  sellerId?: number
): Promise<KsefSettingsResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('password', password);
  if (sellerId != null) form.append('seller_id', String(sellerId));
  const res = await apiClient.post<KsefSettingsResponse>('/api/ksef/settings/certificate', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function enrollKsefCertificate(
  qualifiedCert: File,
  qualifiedPassword: string,
  certificateName: string,
  sellerId?: number
): Promise<KsefSettingsResponse> {
  const form = new FormData();
  form.append('file', qualifiedCert);
  form.append('password', qualifiedPassword);
  form.append('certificate_name', certificateName);
  if (sellerId != null) form.append('seller_id', String(sellerId));
  const res = await apiClient.post<KsefSettingsResponse>(
    '/api/ksef/enroll-certificate',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
  );
  return res.data;
}

export async function testKsefConnection(sellerId?: number): Promise<KsefSettingsResponse & { connected: boolean }> {
  const res = await apiClient.post('/api/ksef/test-connection', { seller_id: sellerId ?? null });
  return res.data;
}

export async function disconnectKsef(sellerId?: number): Promise<KsefSettingsResponse & { connected: boolean }> {
  const res = await apiClient.post('/api/ksef/disconnect', { seller_id: sellerId ?? null });
  return res.data;
}

export async function submitInvoiceToKsef(
  invoiceId: number
): Promise<{ invoice_id: number; ksef: KsefInvoiceState }> {
  const res = await apiClient.post(`/api/ksef/invoices/${invoiceId}/submit`, {}, { timeout: 180000 });
  return res.data;
}

export async function getKsefInvoiceStatus(
  invoiceId: number
): Promise<{ invoice_id: number; ksef: KsefInvoiceState; events: KsefActivityEvent[] }> {
  const res = await apiClient.get(`/api/ksef/invoices/${invoiceId}/status`);
  return res.data;
}

export async function bulkSubmitToKsef(
  invoiceIds: number[]
): Promise<{ accepted: number; rejected: number; errors: number; results: any[] }> {
  const res = await apiClient.post('/api/ksef/bulk-submit', { invoice_ids: invoiceIds }, { timeout: 600000 });
  return res.data;
}

export async function downloadKsefUpo(invoiceId: number, invoiceNumber: string): Promise<void> {
  const res = await apiClient.get(`/api/ksef/invoices/${invoiceId}/upo`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `UPO_${invoiceNumber}.xml`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Parse a blob/JSON axios error into a readable message. */
export async function parseKsefError(err: any): Promise<string> {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      return JSON.parse(text)?.detail || text;
    } catch {
      return 'KSeF request failed.';
    }
  }
  return data?.detail || err?.message || 'KSeF request failed.';
}
